import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "../v3/theme.css";
import { caseStudies } from "../v3/caseStudies";

const bodyStyle = { fontSize: 15, lineHeight: 1.75, color: "var(--muted)", margin: 0 };

const Pipeline = ({ items }) => (
  <div className="fx wrap ac" style={{ gap: 8, margin: "18px 0 4px" }}>
    {items.map((p, i) => (
      <React.Fragment key={p}>
        <span className="tag">{p}</span>
        {i < items.length - 1 && (
          <span className="mono" style={{ color: "var(--accent)", fontSize: 13 }}>
            →
          </span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <div className="col" style={{ gap: 16, marginTop: 18 }}>
    {items.map((st, i) => (
      <div className="fx" style={{ gap: 14 }} key={st.title}>
        <span
          className="mono"
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "var(--accent)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          {i + 1}
        </span>
        <p style={bodyStyle}>
          <strong style={{ color: "var(--ink)", fontWeight: 600 }}>{st.title}</strong> — {st.body}
        </p>
      </div>
    ))}
  </div>
);

const MiniCards = ({ items }) => (
  <div className="cs-cards" style={{ marginTop: 18 }}>
    {items.map((m) => (
      <div key={m.title} style={{ background: "var(--soft)", border: "1px solid var(--line)", borderRadius: 10, padding: 18 }}>
        {m.subtitle && (
          <div className="mono" style={{ fontSize: 10.5, letterSpacing: ".1em", color: "var(--accent)", marginBottom: 6 }}>
            {m.subtitle}
          </div>
        )}
        <p style={{ fontSize: 14.5, fontWeight: 600, margin: "0 0 8px", color: "var(--ink)" }}>
          {m.icon} {m.title}
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.65, color: "var(--muted)", margin: 0 }}>{m.body}</p>
      </div>
    ))}
  </div>
);

const Bullets = ({ items }) => (
  <div className="col" style={{ gap: 18, marginTop: 18 }}>
    {items.map((b) => (
      <div className="fx" style={{ gap: 14 }} key={b.title}>
        <span style={{ width: 5, borderRadius: 3, background: "var(--accent)", flexShrink: 0, alignSelf: "stretch" }} />
        <div>
          <p style={{ fontSize: 14.5, fontWeight: 600, margin: "0 0 4px", color: "var(--ink)" }}>{b.title}</p>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: "var(--muted)", margin: 0 }}>{b.body}</p>
        </div>
      </div>
    ))}
  </div>
);

const SectionCard = ({ s }) => (
  <div className="card" style={{ padding: "34px 36px", height: "100%", boxSizing: "border-box" }}>
    <div className="snum" style={{ marginBottom: 8 }}>[ {s.label.toUpperCase()} ]</div>
    <h2 className="disp" style={{ fontSize: 23, fontWeight: 700, margin: "0 0 14px", letterSpacing: "-.02em", lineHeight: 1.2 }}>
      {s.heading}
    </h2>
    {s.body && <p style={bodyStyle}>{s.body}</p>}
    {s.pipeline && <Pipeline items={s.pipeline} />}
    {s.screens && (
      <div className="cs-pair" style={{ marginTop: 18 }}>
        {s.screens.map((sc) => (
          <figure key={sc.src} style={{ margin: 0 }}>
            <img
              src={sc.src}
              alt={sc.caption}
              style={{ width: "100%", display: "block", borderRadius: 10, border: "1px solid var(--line)" }}
            />
            <figcaption className="mono" style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 8, letterSpacing: ".06em" }}>
              {sc.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    )}
    {s.steps && <Steps items={s.steps} />}
    {s.cards && <MiniCards items={s.cards} />}
    {s.bullets && <Bullets items={s.bullets} />}
    {s.features && (
      <div className="col" style={{ gap: 10, marginTop: 16 }}>
        {s.features.map((f) => (
          <span key={f} className="check" style={{ fontWeight: 500 }}>
            {f}
          </span>
        ))}
      </div>
    )}
    {s.stack && (
      <div className="col" style={{ gap: 8, marginTop: 16 }}>
        {s.stack.map((r) => (
          <p key={r.label} style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: "var(--muted)" }}>
            <span className="mono" style={{ color: "var(--accent)", fontSize: 12.5, fontWeight: 500 }}>{r.label}</span>
            {" — "}
            {r.value}
          </p>
        ))}
      </div>
    )}
    {s.highlight && (
      <div
        style={{
          borderLeft: "3px solid var(--accent)",
          background: "var(--accent-soft)",
          padding: "14px 18px",
          borderRadius: "0 10px 10px 0",
          fontStyle: "italic",
          fontSize: 14.5,
          lineHeight: 1.7,
          color: "#334155",
          marginTop: 18,
        }}
      >
        {s.highlight}
      </div>
    )}
    {s.body2 && <p style={{ ...bodyStyle, marginTop: 18 }}>{s.body2}</p>}
  </div>
);

const ProjectCaseStudy = () => {
  const { slug } = useParams();
  const cs = caseStudies[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ autoRaf: true, duration: 1.15 });
    // late layout shifts (fonts) can nudge the restored scroll — pin it to top
    const t = setTimeout(() => lenis.scrollTo(0, { immediate: true }), 250);
    return () => {
      clearTimeout(t);
      lenis.destroy();
    };
  }, [slug]);

  if (!cs) return <Navigate to="/" replace />;

  return (
    <>
      <Helmet>
        <title>{cs.title.split("—")[0].trim()} — Case Study | Mohsin Idrees</title>
        <meta name="description" content={cs.subtitle} />
      </Helmet>

      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,.88)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="pagewrap fx jb ac" style={{ height: 72 }}>
          <Link to="/" className="mono fw6" style={{ fontSize: 14.5, color: "var(--ink)" }}>
            ~/mohsin-idrees<span style={{ color: "var(--accent)" }}>_</span>
          </Link>
          <div className="fx ac gap24">
            <Link to="/#work" className="navlink">
              ← back_to_work
            </Link>
            <Link to="/#contact" className="btn btn-primary" style={{ padding: "10px 20px", fontSize: 13.5 }}>
              Book a call<span className="arr">→</span>
            </Link>
          </div>
        </div>
      </nav>

      <header style={{ padding: "80px 0 56px", background: "linear-gradient(160deg, #ffffff 55%, var(--accent-soft) 100%)" }}>
        <div className="pagewrap">
          <div className="snum" style={{ marginBottom: 16 }}>[ CASE STUDY ]</div>
          <div className="fx wrap gap8 mb20">
            {cs.tags.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </div>
          <h1 style={{ fontSize: "clamp(30px, 3.6vw, 46px)", lineHeight: 1.12, letterSpacing: "-.025em", fontWeight: 700, margin: "0 0 18px", maxWidth: 900 }}>
            {cs.title}
          </h1>
          <p className="lead" style={{ maxWidth: 760, margin: 0 }}>
            {cs.subtitle}
          </p>
          {cs.liveUrl && (
            <a href={cs.liveUrl} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ padding: "12px 24px", fontSize: 14, marginTop: 26 }}>
              View live site<span className="arr">↗</span>
            </a>
          )}
        </div>
      </header>

      <main className="pagewrap" style={{ paddingBottom: 90 }}>
        <div className="cs-stats mb24">
          {cs.stats.map((s) => (
            <div key={s.label} style={{ background: "var(--accent-soft)", border: "1px solid var(--accent-line)", borderRadius: 12, padding: "18px 20px" }}>
              <div className="disp fw7" style={{ fontSize: 24, color: "var(--accent)", letterSpacing: "-.02em" }}>{s.num}</div>
              <div className="mono" style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, letterSpacing: ".06em" }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
        {cs.heroImage && (
          <div className="card mb24" style={{ padding: 10, overflow: "hidden" }}>
            <img src={cs.heroImage.src} alt={cs.heroImage.alt} style={{ width: "100%", display: "block", borderRadius: 8 }} />
          </div>
        )}
        <div className="col" style={{ gap: 20 }}>
          {cs.sections.map((s, i) =>
            s.pair ? (
              <div className="cs-pair" key={i}>
                <SectionCard s={s.pair[0]} />
                <SectionCard s={s.pair[1]} />
              </div>
            ) : (
              <SectionCard s={s} key={i} />
            )
          )}
        </div>
      </main>

      <section
        className="tc"
        style={{
          padding: "90px 0",
          backgroundColor: "var(--ink)",
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      >
        <div className="pagewrap">
          <div className="mono" style={{ fontSize: 13, color: "#7DD3FC", marginBottom: 18 }}>$ book --call mohsin-idrees</div>
          <h2 className="disp" style={{ fontSize: "clamp(28px, 3.4vw, 44px)", fontWeight: 700, letterSpacing: "-.025em", color: "#fff", margin: "0 0 14px" }}>
            Want something like this?
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: "#94A3B8", maxWidth: 480, margin: "0 auto 34px" }}>
            Tell me about your project and I'll come back with a plan.
          </p>
          <Link to="/#contact" className="btn" style={{ background: "var(--accent)", color: "#fff", padding: "16px 34px", fontSize: 15 }}>
            Book a call<span className="arr">→</span>
          </Link>
        </div>
      </section>

      <div className="pagewrap fx jb ac wrap gap12 mono" style={{ padding: "24px 0", color: "var(--muted)", fontSize: 12 }}>
        <div>© {new Date().getFullYear()} MOHSIN IDREES — ALL RIGHTS RESERVED</div>
        <Link to="/#work" className="mono fs13">
          ← back_to_work
        </Link>
      </div>
    </>
  );
};

export default ProjectCaseStudy;
